import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StaffUser } from '../types';
import Logo from './Logo';
import ForgotPassword from './ForgotPassword';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempUser, setTempUser] = useState<StaffUser | null>(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const { login, updatePassword } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user) {
        if (user.mustChangePassword) {
          setTempUser(user);
          setLoading(false);
        } else {
          navigate('/');
        }
      } else {
        setError('Invalid credentials. Access restricted to authorized office staff.');
        setLoading(false);
      }
    } catch (err) {
      console.error("Login catch block triggered:", err);
      setError('Database connection error. Local storage might be corrupted or blocked.');
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempUser) return;
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(newPassword, tempUser);
      navigate('/');
    } catch (err) {
      setError("Failed to update credentials.");
      setLoading(false);
    }
  };

  if (tempUser) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 relative overflow-hidden">
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden">
            <div className="p-10 pb-6 text-center">
              <div className="bg-amber-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-600 border border-amber-100">
                <i className="fas fa-key-skeleton text-3xl"></i>
              </div>
              <h2 className="font-serif text-2xl font-bold text-slate-900">Security Update Required</h2>
              <p className="text-slate-500 font-medium text-sm mt-3">Welcome, <span className="text-slate-900 font-bold">{tempUser.name}</span>. As a {tempUser.role}, you must set a private password before accessing the firm database.</p>
            </div>

            <form onSubmit={handleUpdatePassword} className="p-10 pt-4 space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-4 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">New Secure Password</label>
                <input 
                  type="password" required
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-600 outline-none transition-all font-medium"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Confirm New Password</label>
                <input 
                  type="password" required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-600 outline-none transition-all font-medium"
                  placeholder="Repeat new password"
                />
              </div>

              <button 
                type="submit" disabled={loading}
                className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 uppercase tracking-widest text-xs"
              >
                {loading ? <i className="fas fa-circle-notch animate-spin"></i> : "Initialize Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decor - Minimalist */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-slate-50 rounded-full -mr-96 -mt-96"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-slate-50 rounded-full -ml-48 -mb-48"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden">
          <div className="p-10 pb-6 flex flex-col items-center">
            <div className="bg-white p-6 rounded-2xl mb-8 border border-slate-50 shadow-sm">
              <Logo className="h-32" />
            </div>
            <h2 className="font-serif text-3xl font-bold text-slate-900 mt-2">Staff Portal</h2>
            <p className="text-slate-400 font-medium text-sm mt-2 uppercase tracking-widest">Authorized Access Only</p>
          </div>

          <form onSubmit={handleLogin} className="p-10 pt-4 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-4 rounded-xl text-sm font-medium border border-red-100">
                <i className="fas fa-exclamation-circle text-lg mr-2"></i> {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Office Email</label>
              <div className="relative">
                <span className="absolute left-4 top-4 text-slate-400"><i className="fas fa-envelope"></i></span>
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="staff@lluislaw.com"
                  className="w-full pl-11 pr-4 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 outline-none transition-all font-medium text-slate-800"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Secure Password</label>
              <div className="relative">
                <span className="absolute left-4 top-4 text-slate-400"><i className="fas fa-lock"></i></span>
                <input 
                  type="password" 
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 outline-none transition-all font-medium text-slate-800"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 disabled:opacity-70 flex justify-center items-center gap-3 text-sm uppercase tracking-widest mt-4"
            >
              {loading ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-shield-check"></i>}
              Enter Secure Environment
            </button>

                        <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium hover:underline transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            <div className="bg-amber-50/50 p-4 rounded-2xl border border-amber-100/50 mt-6">
               <div className="flex items-center gap-2 mb-2">
                 <i className="fas fa-info-circle text-amber-700 text-xs"></i>
                 <p className="text-[10px] text-amber-700 font-bold uppercase tracking-widest">Demo Credentials</p>
               </div>
               <p className="text-[11px] text-amber-600/80 font-medium">Email: <span className="text-amber-800 font-bold">karl@lluislaw.com</span> | Pass: <span className="text-amber-800 font-bold">admin</span></p>
            </div>
          </form>
          
          <div className="p-8 bg-slate-50 border-t border-slate-100 text-center">
             <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">
               Lluis Law Professional Corporation © {new Date().getFullYear()}
             </p>
          </div>
        </div>
      </div>
            <ForgotPassword isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </div>
  );
};

export default Login;
